import React from "react"
import ForecastQualityBadge from "./ForecastQualityBadge"
import { type CustomCellRendererProps } from "ag-grid-react"
import { filterDefined } from "~/utils/tsUtils"
import { formatName } from "~/utils/namingUtils"
import { mean } from "lodash"
import InfoTooltip from "../reusable/InfoTooltip"

interface QualityCellRendererProps extends CustomCellRendererProps {
  evaluationMap?: Map<
    string,
    {
      lowSales: boolean | undefined
      overallScore: number | null
      oneMonthScore: number | null
      threeMonthsScore: number | null
      sixMonthsScore: number | null
      twelveMonthsScore: number | null
    }
  >
  /**
   * PO/buy contexts pass the warehouses the PO orders into; forwarded to the badge to scope (and
   * enable) the lead-time panel. Omitted in non-PO tables => no lead-time panel.
   */
  warehouseIds?: string[]
}

export const QualityCellRenderer = (params: QualityCellRendererProps) => {
  const { evaluationMap, node, value, warehouseIds } = params
  if (value === null || value === undefined) return "-"

  const variantIds: string[] =
    (node?.group
      ? filterDefined(node?.allLeafChildren?.map((node) => node.data?.variantId) ?? [])
      : [node?.data?.variantId]) ?? []

  // When the grid is expanded to a SKU-level leaf row, scope the lead-time panel to that SKU
  // (forecast = variant forecast x SKU split weight, sales = that SKU's own sales). Variant quality
  // scores above stay variant-level (the SKU inherits its variant's quality).
  const skuId: string | undefined = !node?.group ? node?.data?.skuId : undefined

  const evaluationValues = variantIds
    .map((id) => evaluationMap?.get(id))
    .filter((e) => e?.overallScore !== null && e?.overallScore !== undefined)

  // If all values are null/undefined, return null
  if (evaluationValues.length === 0) {
    return null
  }

  const meanScore = (
    getter: (e: (typeof evaluationValues)[number]) => number | null | undefined,
    scale = 1,
  ) => {
    const values = evaluationValues
      .map(getter)
      .filter((score): score is number => score !== null && score !== undefined)
    return values.length > 0 ? mean(values) * scale : null
  }

  const oneMonthScore = meanScore((e) => e?.oneMonthScore)
  const threeMonthsScore = meanScore((e) => e?.threeMonthsScore)
  const sixMonthsScore = meanScore((e) => e?.sixMonthsScore)
  const twelveMonthsScore = meanScore((e) => e?.twelveMonthsScore)
  // Derive the overall score from the gathered variant scores (not params.value) so the
  // icon stays consistent with the filter's bucketing regardless of AG Grid agg config.
  const overallScore = meanScore((e) => e?.overallScore, 100)

  return (
    <div className="flex items-center h-11 gap-1">
      <ForecastQualityBadge
        quality={overallScore}
        oneMonthScore={oneMonthScore}
        threeMonthsScore={threeMonthsScore}
        sixMonthsScore={sixMonthsScore}
        twelveMonthsScore={twelveMonthsScore}
        variantIds={variantIds}
        skuId={skuId}
        warehouseIds={warehouseIds}
      />
      {evaluationValues?.some((x) => x?.lowSales === true) && (
        <InfoTooltip
          className="h-4 w-4 text-violet-500"
          body={
            <div>
              <p>
                Low {formatName("sale", true)} detected for{" "}
                {evaluationValues.filter((x) => x?.lowSales === true).length} underlying variant(s).
              </p>
              <p>Quality can be misleading in cases with low {formatName("sale", true)}</p>
            </div>
          }
        />
      )}
    </div>
  )
}
